import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Organization, OrgPart } from '@newgenus/common';

import { OrgBoardService } from '../../../services/org-board-service';
import {
    BuilderConfig,
    BuilderSavePayload,
    BuilderTreeNode,
    buildBuilderNodeFromOrgPart,
    extractIndexFromPath,
} from '../../../shared/org-part-tree-builder/builder-models';
import { OrgPartTreeBuilderComponent } from '../../../shared/org-part-tree-builder/org-part-tree-builder.component';

const DEFAULT_COLOUR = { background: '#000000', color: '#ffffff', linkColor: '#000000' };

/**
 * Level-aware allowed-children resolver shared between this wrapper and the
 * shared builder. The two-tier executive rule (executive at level 0-1 →
 * `[executive, post]`, level 2+ → `[division, post]`) is bulk-only; the edit
 * dialog uses a flat ALLOWED_CHILDREN map.
 */
function bulkAllowedChildTypes(parentType: string, level = 0): string[] {
    switch ((parentType || '').toLowerCase()) {
        case 'executive':
            return level <= 1 ? ['executive', 'post'] : ['division', 'post'];
        case 'division':
            return ['department', 'post'];
        case 'department':
            return ['section', 'post'];
        case 'section':
            return ['post', 'unit'];
        case 'unit':
            return ['post'];
        case 'post':
            return [];
        default:
            return ['post'];
    }
}

/**
 * Bulk-Create-Position dialog wrapper. Behaviour and UI now live in
 * `OrgPartTreeBuilderComponent`; this wrapper is responsible for:
 *   - exposing the legacy `@Input` set so its existing host dialog
 *     (`org-create-position.dialog.ts`) keeps working without changes,
 *   - building the initial multi-root `BuilderTreeNode[]` from `existingSiblings`
 *     plus their descendants found in `allOrgParts`,
 *   - composing the `BuilderConfig` (multi-root mode, MAX_POSITIONS=50,
 *     level-aware `getAllowedChildTypes`, bulk-flavoured labels and chips,
 *     `saveCommand` closure over `obService.bulkSave`, and a `refreshTree`
 *     callback that re-derives the tree from the now-updated organization),
 *   - emitting `closed` to the parent dialog.
 */
@Component({
    selector: 'shared-org-bulk-create-position',
    imports: [OrgPartTreeBuilderComponent],
    template: `
        <lib-org-part-tree-builder
            [initialTree]="initialTree"
            [config]="config"
            [partTypes]="partTypes"
            [isDeveloper]="false"
            (closeRequested)="onClose($event)"
        >
        </lib-org-part-tree-builder>
    `,
})
export class OrgBulkCreatePositionComponent implements OnInit {
    @Input() partTypes: string[] = ['executive', 'division', 'department', 'section', 'unit', 'post'];
    @Input() anchorPartName = '';
    @Input() anchorPartKey = '';
    @Input() organization!: Organization;
    @Input() ancestorBreadcrumb: { partKey: string; name: string; partType: string }[] = [];
    @Input() defaultColour: { color: string; background: string; linkColor: string } = { ...DEFAULT_COLOUR };
    @Input() insertionType: 'child' | 'sibling' = 'child';
    /** Existing siblings/children at the anchor — render-only reference subtrees. */
    @Input() existingSiblings: any[] = [];
    /** Full orgParts array — used to recursively build subtrees for existing nodes. */
    @Input() allOrgParts: any[] = [];

    @Output() closed = new EventEmitter<{ saved: boolean }>();

    private obService = inject(OrgBoardService);
    private hostEl = inject(ElementRef) as ElementRef<HTMLElement>;

    initialTree: BuilderTreeNode[] = [];
    config!: BuilderConfig;

    /** Deduplicated orgParts used by the existing-subtree builder. */
    private dedupedOrgParts: OrgPart[] = [];

    ngOnInit(): void {
        this.dedupedOrgParts = this.dedupOrgParts(this.allOrgParts);
        this.initialTree = this.buildInitialTree();
        this.config = this.buildConfig();
    }

    // ─────────────────────────────────────────────────────────────
    // Tree building
    // ─────────────────────────────────────────────────────────────

    /**
     * Build the initial multi-root tree from `existingSiblings`. Each existing
     * sibling becomes a root-level reference subtree (recursive descendants
     * pulled from `dedupedOrgParts`). New nodes added by the user appear here
     * too as the session progresses.
     */
    private buildInitialTree(): BuilderTreeNode[] {
        const roots: BuilderTreeNode[] = [];
        const seen = new Set<string>();

        for (const existing of this.existingSiblings || []) {
            const data = existing?.data ?? existing;
            const partKey = data?.partKey || data?.key;
            if (!partKey || partKey === 'phantom' || `${partKey}`.startsWith('bulk-')) continue;
            if (seen.has(partKey)) continue;
            seen.add(partKey);

            // Find the canonical OrgPart so we can use the shared adapter.
            const canonical =
                this.dedupedOrgParts.find((p) => p.partKey === partKey) || this.toOrgPartShim(data, partKey);
            if (!canonical) continue;

            const node = buildBuilderNodeFromOrgPart(
                canonical,
                this.dedupedOrgParts,
                0,
                extractIndexFromPath,
                this.defaultColour as { background: string; color: string; linkColor: string },
                true // collapsed by default for reference subtrees
            );
            roots.push(node);
        }
        return roots;
    }

    /**
     * Defensive shim: `existingSiblings` items can be either thin wrappers with
     * a `.data` property or raw OrgPart-shaped objects. When the part isn't
     * found in `allOrgParts` (rare race condition) we still need a node so the
     * UI doesn't render an empty list.
     */
    private toOrgPartShim(data: any, partKey: string): OrgPart | null {
        if (!data) return null;
        return {
            partKey,
            name: data.name || 'Unnamed',
            subTitle: data.subTitle || '',
            partType: data.partType || '',
            parentPartKey: data.parentPartKey || this.anchorPartKey,
            colour: data.colour || { ...this.defaultColour },
            vfp: data.vfp,
            headerRole: data.headerRole,
            depth: data.depth ?? 0,
            id: data.id ?? 0,
            path: data.path || '',
        } as OrgPart;
    }

    private dedupOrgParts(parts: any[]): OrgPart[] {
        const seen = new Set<string>();
        const out: OrgPart[] = [];
        for (const p of parts || []) {
            const key = p?.partKey || p?.key;
            if (!key || key === 'phantom' || `${key}`.startsWith('bulk-')) continue;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(p as OrgPart);
        }
        return out;
    }

    // ─────────────────────────────────────────────────────────────
    // Config
    // ─────────────────────────────────────────────────────────────

    private buildConfig(): BuilderConfig {
        return {
            mode: 'bulk',
            minDepth: 0,
            // No protected root in bulk mode — every node is draggable.
            protectedRootPartKey: undefined,
            showAddRootButton: true,
            maxPositions: 50,
            getAllowedChildTypes: bulkAllowedChildTypes,
            defaultColour: {
                background: this.defaultColour.background,
                color: this.defaultColour.color,
                linkColor: this.defaultColour.linkColor,
            },
            // Bulk hides the subtitle field for new nodes (subtitle is reserved
            // for already-saved parts in the bulk UX).
            hideSubtitleForNewNodes: true,
            anchorPartKey: this.anchorPartKey,
            insertionType: this.insertionType,
            // Bulk supplies its own breadcrumb via @Input; the anchor row is
            // already the last item, so the chip explicitly marks the new
            // children/siblings.
            breadcrumb: (this.ancestorBreadcrumb || []).map((b) => ({
                partKey: b.partKey,
                name: b.name,
                partType: b.partType,
            })),
            showInsertionPointChip: true,
            headerSubtitle: this.anchorPartName ? `adding under ${this.anchorPartName}` : '',
            headerIcon: 'playlist_add',
            headerTitle: 'Bulk Create Positions',
            saveButtonLabel: 'Create',
            // Bulk never shows the developer footer — wrappers' data is too
            // heterogeneous (no single root part to inspect).
            showDeveloperInfo: false,
            organization: this.organization,
            // Bulk has no equivalent of edit's post→container warning.
            preSave: undefined,
            saveCommand: (payload) => this.saveCommand(payload),
            refreshTree: (collapsedKeys) => this.refreshTree(collapsedKeys),
        };
    }

    // ─────────────────────────────────────────────────────────────
    // Hooks
    // ─────────────────────────────────────────────────────────────

    private async saveCommand(payload: BuilderSavePayload): Promise<void> {
        const orgKey = this.organization?.key;
        if (!orgKey) throw new Error('Bulk-Create dialog: organization key is required');
        const { expectedNewHatCount: _enh, reorderOnlySave: _ros, ...servicePayload } = payload;
        await this.obService.bulkSave(orgKey, servicePayload);
    }

    /**
     * Continue-Editing refresh: the realtime listener on `OrgBoardService` has
     * applied backend updates by now, so we re-read the org and rebuild the
     * existing-siblings list at the anchor before reconstructing the tree.
     */
    private async refreshTree(_collapsedKeys: Map<string, boolean>): Promise<{
        initialTree: BuilderTreeNode[];
        breadcrumb?: { partKey?: string; name: string; partType: string }[];
    } | null> {
        const freshOrg = this.obService.organization;
        if (freshOrg) this.organization = freshOrg;

        const orgParts = this.organization?.orgParts || [];
        this.allOrgParts = orgParts;
        this.dedupedOrgParts = this.dedupOrgParts(orgParts);

        // Recompute existingSiblings from the fresh org: children of the anchor.
        this.existingSiblings = this.dedupedOrgParts.filter((p) => p.parentPartKey === this.anchorPartKey);

        const initialTree = this.buildInitialTree();
        return {
            initialTree,
            breadcrumb: (this.ancestorBreadcrumb || []).map((b) => ({
                partKey: b.partKey,
                name: b.name,
                partType: b.partType,
            })),
        };
    }

    // ─────────────────────────────────────────────────────────────
    // Close
    // ─────────────────────────────────────────────────────────────

    onClose(event: { saved: boolean }): void {
        this.closed.emit(event);
    }
}
